import React, { useEffect, useState } from "react";
import { Table, Tag, Button, Modal, Typography, Space, Empty, message, Descriptions, Popconfirm } from "antd";
import { ReloadOutlined, EyeOutlined, CheckCircleOutlined, CloseCircleOutlined, CloudSyncOutlined, FileTextOutlined } from "@ant-design/icons";
import { t } from "i18next";
import ApiService from "../../../core/service/ApiService";

const { Text, Paragraph } = Typography;

export default function Hl7MessagesPreview({ appointmentId }) {
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [sendingReportId, setSendingReportId] = useState(null);
    const [selectedMessage, setSelectedMessage] = useState(null);
    const [detailModalVisible, setDetailModalVisible] = useState(false);
    const [teletipStatusLoading, setTeletipStatusLoading] = useState(false);
    const [teletipStatusList, setTeletipStatusList] = useState([]);
    const [teletipStatusModalVisible, setTeletipStatusModalVisible] = useState(false);

    const fetchMessages = async () => {
        if (!appointmentId) return;
        setLoading(true);
        try {
            const res = await ApiService.AppointmentTeletip.GetHl7Messages(appointmentId);
            if (res.success) {
                setMessages(res.data || []);
            }
        } finally {
            setLoading(false);
        }
    };

    const sendReportMessage = async (messageId) => {
        setSendingReportId(messageId);
        try {
            const res = await ApiService.AppointmentTeletip.SendReportMessage(messageId);
            if (res.success) {
                message.success(t('Report message sent successfully'));
                await fetchMessages();
            }
        } finally {
            setSendingReportId(null);
        }
    };

    const viewMessageDetail = async (record) => {
        const res = await ApiService.AppointmentTeletip.GetHl7Message(record.id);
        if (res.success) {
            setSelectedMessage(res.data);
            setDetailModalVisible(true);
        }
    };

    const fetchTeletipStatus = async () => {
        if (!appointmentId) return;
        setTeletipStatusLoading(true);
        try {
            const res = await ApiService.AppointmentTeletip.GetTeletipOrderStatus(appointmentId);
            if (res.success) {
                setTeletipStatusList(res.data || []);
                setTeletipStatusModalVisible(true);
            }
        } finally {
            setTeletipStatusLoading(false);
        }
    };

    useEffect(() => {
        fetchMessages();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appointmentId]);

    const columns = [
        {
            title: t('Operation'),
            dataIndex: 'operationName',
            key: 'operationName',
            width: 180,
        },
        {
            title: t('Accession No'),
            dataIndex: 'accessionNumber',
            key: 'accessionNumber',
            width: 100,
            render: (text) => text || '-'
        },
        {
            title: t('Type'),
            dataIndex: 'typeName',
            key: 'typeName',
            width: 80,
            render: (text, record) => (
                <Tag color={record.type === 1 ? 'blue' : 'green'}>
                    {text}
                </Tag>
            )
        },
        {
            title: t('Order Status'),
            dataIndex: 'success',
            key: 'success',
            width: 100,
            render: (success) => (
                success ? (
                    <Tag icon={<CheckCircleOutlined />} color="success">
                        {t('Sent')}
                    </Tag>
                ) : (
                    <Tag icon={<CloseCircleOutlined />} color="error">
                        {t('Failed')}
                    </Tag>
                )
            )
        },
        {
            title: t('Report Status'),
            key: 'reportStatus',
            width: 120,
            render: (_, record) => {
                if (record.reportSentAt) {
                    return record.reportSuccess ? (
                        <Tag icon={<CheckCircleOutlined />} color="success">
                            {t('Sent')}
                        </Tag>
                    ) : (
                        <Tag icon={<CloseCircleOutlined />} color="error">
                            {t('Failed')}
                        </Tag>
                    );
                }
                return (
                    <Tag color="default">
                        {t('Pending')}
                    </Tag>
                );
            }
        },
        {
            title: t('Report Submission'),
            dataIndex: 'reportSentAt',
            key: 'reportSentAt',
            width: 140,
            render: (date) => date ? new Date(date).toLocaleString('tr-TR') : '-'
        },
        {
            title: t('Order Submission'),
            dataIndex: 'sentAt',
            key: 'sentAt',
            width: 140,
            render: (date) => date ? new Date(date).toLocaleString('tr-TR') : '-'
        },
        {
            title: t('Error'),
            key: 'errors',
            ellipsis: true,
            width: 150,
            render: (_, record) => {
                const error = record.error || record.reportError;
                return error ? (
                    <Text type="danger" ellipsis={{ tooltip: error }}>
                        {error}
                    </Text>
                ) : '-';
            }
        },
        {
            title: t('Actions'),
            key: 'actions',
            width: 200,
            fixed: 'right',
            render: (_, record) => (
                <Space size="small">
                    <Button
                        type="link"
                        size="small"
                        icon={<EyeOutlined />}
                        onClick={() => viewMessageDetail(record)}
                    >
                        {t('Detail')}
                    </Button>
                    {record.success && (
                        <Popconfirm
                            title={t('Send Report')}
                            description={t('Are you sure you want to send the report message to Teletıp?')}
                            onConfirm={() => sendReportMessage(record.id)}
                            okText={t('Send')}
                            cancelText={t('Cancel')}
                        >
                            <Button
                                type="link"
                                size="small"
                                icon={<FileTextOutlined />}
                                loading={sendingReportId === record.id}
                                style={{ color: record.reportSuccess ? '#52c41a' : (record.reportSentAt && !record.reportSuccess) ? '#ff4d4f' : undefined }}
                            >
                                {record.reportSuccess ? t('Resend') : (record.reportSentAt && !record.reportSuccess) ? t('Retry') : t('Send Report')}
                            </Button>
                        </Popconfirm>
                    )}
                </Space>
            )
        }
    ];

    return (
        <div>
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Space>
                    <Button
                        icon={<ReloadOutlined />}
                        onClick={fetchMessages}
                        loading={loading}
                    >
                        {t('Refresh')}
                    </Button>
                    <Button
                        type="primary"
                        icon={<CloudSyncOutlined />}
                        onClick={fetchTeletipStatus}
                        loading={teletipStatusLoading}
                    >
                        {t('Query Teletıp Status')}
                    </Button>
                </Space>
            </div>

            {messages.length === 0 && !loading ? (
                <Empty description={t('No HL7 messages found')} />
            ) : (
                <Table
                    dataSource={messages}
                    columns={columns}
                    rowKey="id"
                    loading={loading}
                    size="small"
                    pagination={{ pageSize: 10 }}
                    scroll={{ x: 1400 }}
                />
            )}

            <Modal
                title={t('HL7 Message Detail')}
                open={detailModalVisible}
                onCancel={() => setDetailModalVisible(false)}
                footer={null}
                width={800}
            >
                {selectedMessage && (
                    <div>
                        <div style={{ marginBottom: 16 }}>
                            <Space>
                                <Text strong>{t('Operation')}:</Text>
                                <Text>{selectedMessage.operationName}</Text>
                            </Space>
                        </div>
                        <div style={{ marginBottom: 16 }}>
                            <Space>
                                <Text strong>{t('Type')}:</Text>
                                <Tag color={selectedMessage.type === 1 ? 'blue' : 'green'}>
                                    {selectedMessage.typeName}
                                </Tag>
                                <Text strong>{t('Status')}:</Text>
                                {selectedMessage.success ? (
                                    <Tag icon={<CheckCircleOutlined />} color="success">{t('Success')}</Tag>
                                ) : (
                                    <Tag icon={<CloseCircleOutlined />} color="error">{t('Failed')}</Tag>
                                )}
                            </Space>
                        </div>

                        <div style={{ marginBottom: 16 }}>
                            <Text strong>{t('HL7 Message')}:</Text>
                            <Paragraph
                                copyable
                                style={{
                                    backgroundColor: '#f5f5f5',
                                    padding: 12,
                                    borderRadius: 4,
                                    fontFamily: 'monospace',
                                    fontSize: 12,
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-all',
                                    maxHeight: 200,
                                    overflow: 'auto'
                                }}
                            >
                                {selectedMessage.message}
                            </Paragraph>
                        </div>

                        {selectedMessage.ackMessage && (
                            <div style={{ marginBottom: 16 }}>
                                <Text strong>{t('ACK Response')}:</Text>
                                <Paragraph
                                    copyable
                                    style={{
                                        backgroundColor: '#f6ffed',
                                        padding: 12,
                                        borderRadius: 4,
                                        fontFamily: 'monospace',
                                        fontSize: 12,
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-all',
                                        maxHeight: 200,
                                        overflow: 'auto'
                                    }}
                                >
                                    {selectedMessage.ackMessage}
                                </Paragraph>
                            </div>
                        )}

                        {selectedMessage.error && (
                            <div style={{ marginBottom: 16 }}>
                                <Text strong type="danger">{t('Order Error')}:</Text>
                                <Paragraph
                                    style={{
                                        backgroundColor: '#fff2f0',
                                        padding: 12,
                                        borderRadius: 4,
                                        border: '1px solid #ffccc7',
                                        color: '#cf1322'
                                    }}
                                >
                                    {selectedMessage.error}
                                </Paragraph>
                            </div>
                        )}

                        {/* Rapor Bölümü */}
                        {selectedMessage.reportMessage ? (
                            <>
                                <div style={{ marginBottom: 16, marginTop: 24, borderTop: '1px solid #f0f0f0', paddingTop: 16 }}>
                                    <Space>
                                        <Text strong style={{ fontSize: 16 }}>{t('Report Message Details')}</Text>
                                        {selectedMessage.reportSuccess ? (
                                            <Tag icon={<CheckCircleOutlined />} color="success">{t('Sent')}</Tag>
                                        ) : selectedMessage.reportSentAt ? (
                                            <Tag icon={<CloseCircleOutlined />} color="error">{t('Failed')}</Tag>
                                        ) : null}
                                    </Space>
                                </div>

                                <div style={{ marginBottom: 16 }}>
                                    <Text strong>{t('Report HL7 Message')}:</Text>
                                    <Paragraph
                                        copyable
                                        style={{
                                            backgroundColor: '#e6f7ff',
                                            padding: 12,
                                            borderRadius: 4,
                                            fontFamily: 'monospace',
                                            fontSize: 12,
                                            whiteSpace: 'pre-wrap',
                                            wordBreak: 'break-all',
                                            maxHeight: 200,
                                            overflow: 'auto'
                                        }}
                                    >
                                        {selectedMessage.reportMessage}
                                    </Paragraph>
                                </div>

                                {selectedMessage.reportAckMessage && (
                                    <div style={{ marginBottom: 16 }}>
                                        <Text strong>{t('Report ACK Response')}:</Text>
                                        <Paragraph
                                            copyable
                                            style={{
                                                backgroundColor: '#f6ffed',
                                                padding: 12,
                                                borderRadius: 4,
                                                fontFamily: 'monospace',
                                                fontSize: 12,
                                                whiteSpace: 'pre-wrap',
                                                wordBreak: 'break-all',
                                                maxHeight: 200,
                                                overflow: 'auto'
                                            }}
                                        >
                                            {selectedMessage.reportAckMessage}
                                        </Paragraph>
                                    </div>
                                )}

                                {selectedMessage.reportError && (
                                    <div style={{ marginBottom: 16 }}>
                                        <Text strong type="danger">{t('Report Error')}:</Text>
                                        <Paragraph
                                            style={{
                                                backgroundColor: '#fff2f0',
                                                padding: 12,
                                                borderRadius: 4,
                                                border: '1px solid #ffccc7',
                                                color: '#cf1322'
                                            }}
                                        >
                                            {selectedMessage.reportError}
                                        </Paragraph>
                                    </div>
                                )}

                                {selectedMessage.reportSentAt && (
                                    <div style={{ marginBottom: 8 }}>
                                        <Text type="secondary">{t('Report Submission')}: {new Date(selectedMessage.reportSentAt).toLocaleString('tr-TR')}</Text>
                                    </div>
                                )}
                            </>
                        ) : selectedMessage.success && (
                            <div style={{ marginBottom: 16, marginTop: 24, borderTop: '1px solid #f0f0f0', paddingTop: 16 }}>
                                <Space direction="vertical">
                                    <Text strong style={{ fontSize: 16 }}>{t('Report Message')}</Text>
                                    <Text type="secondary">{t('Report has not been sent yet')}</Text>
                                    <Popconfirm
                                        title={t('Send Report')}
                                        description={t('Are you sure you want to send the report message to Teletıp?')}
                                        onConfirm={() => {
                                            sendReportMessage(selectedMessage.id);
                                            setDetailModalVisible(false);
                                        }}
                                        okText={t('Send')}
                                        cancelText={t('Cancel')}
                                    >
                                        <Button 
                                            type="primary" 
                                            icon={<FileTextOutlined />}
                                            loading={sendingReportId === selectedMessage.id}
                                        >
                                            {t('Send Report')}
                                        </Button>
                                    </Popconfirm>
                                </Space>
                            </div>
                        )}

                        <div>
                            <Space>
                                <Text type="secondary">{t('Created')}: {new Date(selectedMessage.createdDate).toLocaleString('tr-TR')}</Text>
                                {selectedMessage.sentAt && (
                                    <Text type="secondary">{t('Order Submission')}: {new Date(selectedMessage.sentAt).toLocaleString('tr-TR')}</Text>
                                )}
                            </Space>
                        </div>
                    </div>
                )}
            </Modal>

            <Modal
                title={t('Teletıp Status Details')}
                open={teletipStatusModalVisible}
                onCancel={() => setTeletipStatusModalVisible(false)}
                footer={null}
                width={900}
            >
                {teletipStatusList.length === 0 ? (
                    <Empty description={t('No status information found')} />
                ) : (
                    teletipStatusList.map((status, index) => (
                        <div key={index} style={{ marginBottom: 24, borderBottom: index < teletipStatusList.length - 1 ? '1px solid #f0f0f0' : 'none', paddingBottom: 16 }}>
                            <Descriptions 
                                title={`Accession No: ${status.accessionNumber}`} 
                                bordered 
                                size="small" 
                                column={2}
                            >
                                <Descriptions.Item label={t('National ID No')}>{status.citizenId || '-'}</Descriptions.Item>
                                <Descriptions.Item label={t('SUT Code')}>{status.sutCode || '-'}</Descriptions.Item>
                                <Descriptions.Item label={t('Teletıp Status')}>
                                    <Tag color={status.teletipStatusId === 1 ? 'success' : status.teletipStatusId === 2 ? 'processing' : 'default'}>
                                        {status.teletipStatus || '-'}
                                    </Tag>
                                </Descriptions.Item>
                                <Descriptions.Item label={t('Medula Status')}>
                                    <Tag color={status.medulaStatusId === 1 ? 'success' : status.medulaStatusId === 2 ? 'warning' : 'default'}>
                                        {status.medulaStatus || '-'}
                                    </Tag>
                                </Descriptions.Item>
                                <Descriptions.Item label={t('WADO Status')}>
                                    <Tag color={status.wadoStatusId === 1 ? 'success' : status.wadoStatusId === 2 ? 'processing' : status.wadoStatusId === 3 ? 'warning' : 'default'}>
                                        {status.wadoStatus || '-'}
                                    </Tag>
                                </Descriptions.Item>
                                <Descriptions.Item label={t('Report Status')}>
                                    <Tag color={status.reportStatusId === 1 ? 'success' : status.reportStatusId === 2 ? 'warning' : 'default'}>
                                        {status.reportStatus || '-'}
                                    </Tag>
                                </Descriptions.Item>
                                <Descriptions.Item label={t('Dose Status')}>
                                    <Tag color={status.doseStatusId === 1 ? 'success' : status.doseStatusId === 2 ? 'warning' : 'default'}>
                                        {status.doseStatus || '-'}
                                    </Tag>
                                </Descriptions.Item>
                                <Descriptions.Item label={t('Duplicate Status')}>
                                    {status.patientHistorySearchStatus || '-'}
                                </Descriptions.Item>
                                <Descriptions.Item label={t('Order Date')}>
                                    {status.requestDate ? new Date(status.requestDate).toLocaleString('tr-TR') : '-'}
                                </Descriptions.Item>
                                <Descriptions.Item label={t('Appointment Date')}>
                                    {status.scheduleDate ? new Date(status.scheduleDate).toLocaleString('tr-TR') : '-'}
                                </Descriptions.Item>
                                <Descriptions.Item label={t('Acquisition Date')}>
                                    {status.performedDate ? new Date(status.performedDate).toLocaleString('tr-TR') : '-'}
                                </Descriptions.Item>
                                <Descriptions.Item label={t('Report Approval Date')}>
                                    {status.reportedDate ? new Date(status.reportedDate).toLocaleString('tr-TR') : '-'}
                                </Descriptions.Item>
                                <Descriptions.Item label={t('Last Medula Submission')}>
                                    {status.lastMedulaSendDate ? new Date(status.lastMedulaSendDate).toLocaleString('tr-TR') : '-'}
                                </Descriptions.Item>
                                <Descriptions.Item label={t('Medula Response Code')}>
                                    {status.medulaResponseCode || '-'}
                                </Descriptions.Item>
                                <Descriptions.Item label={t('WADO Test Count')}>
                                    {status.wadoTestCount ?? '-'}
                                </Descriptions.Item>
                                <Descriptions.Item label={t('Last WADO Test')}>
                                    {status.lastWadoTestDate ? new Date(status.lastWadoTestDate).toLocaleString('tr-TR') : '-'}
                                </Descriptions.Item>
                                {status.medulaResponseMessage && (
                                    <Descriptions.Item label={t('Medula Message')} span={2}>
                                        {status.medulaResponseMessage}
                                    </Descriptions.Item>
                                )}
                                {status.error && (
                                    <Descriptions.Item label={t('Error')} span={2}>
                                        <Text type="danger">{status.error}</Text>
                                    </Descriptions.Item>
                                )}
                                {status.lastWadoResponseMessage && (
                                    <Descriptions.Item label={t('Last WADO Response')} span={2}>
                                        {status.lastWadoResponseMessage}
                                    </Descriptions.Item>
                                )}
                            </Descriptions>
                        </div>
                    ))
                )}
            </Modal>
        </div>
    );
}
